import { IItemResponse, IListResponse } from '@/shared/base/base.dto';
import {
  IChangePassword,
  IConfirmContactChange,
  ISendContactChangeOtp,
  IVerifyContactChangeOtp,
  UserAiHistoryDTO,
  UserDTO,
} from '@/shared';
import { DashboardStatsDTO } from '@/shared/user/dashboard';
import {
  InvoiceResponse,
  UserTransactionDTO,
  UserTransactionSummaryDTO,
} from '@/shared/user/transaction';
import {
  ConflictUser,
  SendContactChangeOtpResult,
  VerifyContactChangeOtpResult,
} from '@/types/personalInfo';
import { ISidebarStats, SidebarStatsParams } from '@/types/sidebarStats';
import { hrApiClient } from '@/lib/api';

export const fetchAuthMe = async (): Promise<UserDTO> => {
  const { data } = await hrApiClient.get<IItemResponse<UserDTO>>('/user');
  return data.data as UserDTO;
};

export const fetchSidebarStats = async (
  params: SidebarStatsParams
): Promise<ISidebarStats> => {
  const { data } = await hrApiClient.get<IItemResponse<ISidebarStats>>(
    '/user/sidebar-stats',
    { params }
  );
  return data.data as ISidebarStats;
};

export const fetchDashboardStats = async (): Promise<DashboardStatsDTO> => {
  const { data } = await hrApiClient.get<DashboardStatsDTO>(
    '/user/dashboard-stats'
  );
  return data ?? {};
};

export const sendContactChangeOtp = async (
  payload: ISendContactChangeOtp
): Promise<SendContactChangeOtpResult> => {
  const { data } = await hrApiClient.post<IItemResponse<{ sent?: boolean }>>(
    '/user/contact-change/send-otp',
    payload
  );

  return {
    success: Boolean(data.data?.sent),
    message: data.message ?? '',
  };
};

export const verifyContactChangeOtp = async (
  payload: IVerifyContactChangeOtp
): Promise<VerifyContactChangeOtpResult> => {
  const { data } = await hrApiClient.post<
    IItemResponse<{ verified?: boolean; conflictUser?: ConflictUser | null }>
  >('/user/contact-change/verify-otp', payload);

  return {
    verified: Boolean(data.data?.verified),
    message: data.message,
    conflictUser: data.data?.conflictUser ?? null,
  };
};

export const confirmContactChange = async (
  payload: IConfirmContactChange
): Promise<void> => {
  await hrApiClient.post('/user/contact-change/confirm', payload);
};

export const createUserInvoice = async (payload: {
  amount: number;
  useZangiaInvoiceCode?: boolean;
  forWhat?: string;
}): Promise<InvoiceResponse> => {
  const { data } = await hrApiClient.post<InvoiceResponse>(
    '/user/invoice',
    payload
  );

  return data;
};

export const fetchUserTransactions = async (
  page = 1,
  limit = 10
): Promise<IListResponse<UserTransactionDTO>> => {
  const { data } = await hrApiClient.get<IListResponse<UserTransactionDTO>>(
    '/user/transactions',
    { params: { page, limit } }
  );

  return data;
};

export const fetchUserTransaction = async (
  transactionId: number
): Promise<UserTransactionDTO> => {
  const { data } = await hrApiClient.get<UserTransactionDTO>(
    `/user/transaction/${transactionId}`
  );
  return data;
};

export const fetchAiHistory = async (
  page = 1,
  limit = 10
): Promise<IListResponse<UserAiHistoryDTO>> => {
  const { data } = await hrApiClient.get<IListResponse<UserAiHistoryDTO>>(
    '/cv/ai/ai-history',
    { params: { page, limit } }
  );
  return data;
};

export const fetchTransactionSummary =
  async (): Promise<UserTransactionSummaryDTO> => {
    const { data } =
      await hrApiClient.get<UserTransactionSummaryDTO>('/user/summary');

    return data;
  };

export const updatePassword = async (
  payload: IChangePassword
): Promise<boolean> => {
  await hrApiClient.post('/auth/change-password', payload);
  return true;
};

export async function cvUpload(data: File | FormData) {
  if (data instanceof File) {
    const formData = new FormData();
    formData.append('files', data);

    return hrApiClient.post<{
      success: boolean;
      url?: string;
      lang_id?: number;
    }>('/cv/ai/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  return hrApiClient.post<{ success: boolean; url?: string; lang_id?: number }>(
    '/cv/ai/upload',
    data,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
}

export async function sendDeleteAccountOtp(params: {
  phone?: string;
  email?: string;
}) {
  return hrApiClient.post('/user/delete/send-otp', params);
}

export async function deleteAccount(
  reason: string,
  code: string,
  params: { phone?: string; email?: string }
) {
  return hrApiClient.post('/user/delete', { reason, code, ...params });
}
